import { useMemo } from 'react';

import useFetchPersonalWallet from 'src/Hooks/Queries/useFetchPersonalWallet';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { SettlementTargetDto } from 'src/Services/API/generated';

/** Below this amount in euros a balance is considered settled (rounding noise). */
const SETTLED_EPSILON = 0.01;

export interface CurrentAssetWalletCounterparty {
  target: SettlementTargetDto;
  netBalance: number;
}

export interface CurrentAssetWallet {
  /** Total owed to the user on the active asset. */
  owedToMe: number;
  /** Total the user owes on the active asset. */
  iOwe: number;
  /** owedToMe - iOwe, on the active asset only. */
  netBalance: number;
  /** Counterparties with a positive net balance towards the user. */
  owedToMeBy: CurrentAssetWalletCounterparty[];
  /** Counterparties the user owes money to. */
  iOweTo: CurrentAssetWalletCounterparty[];
  isSettled: boolean;
  isLoading: boolean;
  isError: boolean;
  /** Refetch the wallet, so a card that failed can retry on its own. */
  refetch: () => void;
}

/**
 * Personal wallet restricted to the asset currently being viewed.
 *
 * The wallet endpoint answers across every asset the user belongs to, but the product
 * invariant is strictly mono-asset: no multi-asset figure is ever shown in the UI. Both
 * "Mon solde" surfaces (home card and Finances header card) go through this hook so they
 * can never drift apart — in particular `myTotalNetBalance` must not be used, it is global.
 */
export const useCurrentAssetWallet = (): CurrentAssetWallet => {
  const assetId = useCurrentAssetId();
  const { data, isLoading, isError, refetch } = useFetchPersonalWallet();

  return useMemo(() => {
    const onCurrentAsset = <T extends { assetId: string }>(settlements: T[] | undefined) =>
      (settlements ?? []).filter(settlement => settlement.assetId === assetId);

    const owedToMeSettlements = onCurrentAsset(data?.owedToMe);
    const iOweSettlements = onCurrentAsset(data?.iOwe);

    const sum = (settlements: { amount: number }[]) =>
      settlements.reduce((total, settlement) => total + settlement.amount, 0);

    const owedToMe = sum(owedToMeSettlements);
    const iOwe = sum(iOweSettlements);

    // A counterparty can sit on both sides; net them so nobody appears twice.
    const balances = new Map<string, CurrentAssetWalletCounterparty>();
    const bump = (target: SettlementTargetDto, delta: number) => {
      const existing = balances.get(target.id);
      if (existing) existing.netBalance += delta;
      else balances.set(target.id, { target, netBalance: delta });
    };
    owedToMeSettlements.forEach(settlement => bump(settlement.from, settlement.amount));
    iOweSettlements.forEach(settlement => bump(settlement.to, -settlement.amount));

    const counterparties = Array.from(balances.values());
    const netBalance = owedToMe - iOwe;

    return {
      owedToMe,
      iOwe,
      netBalance,
      owedToMeBy: counterparties.filter(entry => entry.netBalance > SETTLED_EPSILON),
      iOweTo: counterparties.filter(entry => entry.netBalance < -SETTLED_EPSILON),
      isSettled: Math.abs(netBalance) < SETTLED_EPSILON,
      isLoading,
      isError,
      refetch: () => {
        void refetch();
      },
    };
  }, [data, assetId, isLoading, isError, refetch]);
};

export default useCurrentAssetWallet;
